/**
 * The Business Apartments list (Units redesign, Slice 7).
 *
 * WHAT THIS REPLACES. `ApartmentTable` — thirteen columns inside an
 * `overflow-x-auto` wrapper, the last horizontally scrolling table on this
 * page. With this, neither segment has one.
 *
 * SAME ROW AS CO-LIVING, ON PURPOSE. This renders `UnitListRow`, the component
 * Slice 6 introduced, rather than a second row design. The two segments differ
 * in what they HAVE, not in how they look: a business apartment is one contract
 * rather than a set of rooms, so it carries no per-room occupancy split and the
 * row simply omits that block. The letting date takes that slot instead, which
 * is a field this segment's table carried and co-living's did not.
 *
 * THE POPULATION IS NOT RECOMPUTED HERE. The caller passes the units it already
 * holds. The page splits its segments on a raw `type` comparison that diverges
 * from `normalizeUnitTypeLabel`, so deriving the population here would silently
 * change which units appear — a question this slice does not reopen.
 *
 * THE REVENUE SOURCE IS THE TABLE'S OWN. `currentRevenueChf` reads
 * `unit.current_revenue_chf` for a business apartment, which is exactly what
 * the old table displayed — not co-living's room-price aggregate. That
 * equivalence is pinned by test, because a redesign quietly switching which
 * number means "revenue" would be the expensive kind of mistake.
 */
import React, { useMemo } from "react";

import { buildUnitListItems } from "./unitListModel";
import UnitListRow from "./UnitListRow";

/** Carried over verbatim from the table this replaces: it defines the unit. */
export const BUSINESS_APARTMENTS_NOTE =
  "Einzelne vermietbare Einheiten mit einem Vertrag pro Apartment.";

export default function BusinessApartmentsList({
  units,
  rooms,
  tenancies,
  resolveManagementLabel,
}) {
  const items = useMemo(
    () =>
      buildUnitListItems({
        units,
        rooms,
        tenancies,
        resolveManagementLabel,
      }),
    [units, rooms, tenancies, resolveManagementLabel]
  );

  return (
    <section>
      <p className="mb-3 text-[11px] leading-snug text-muted-foreground">
        {BUSINESS_APARTMENTS_NOTE}
      </p>

      {items.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-border bg-card px-4 py-10 text-center text-[12.5px] text-muted-foreground">
          Keine Business Apartments gefunden.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <UnitListRow key={item.id} item={item} showAvailableFrom />
          ))}
        </div>
      )}
    </section>
  );
}
